import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api } from '../lib/api'
import { deriveProgress } from '../lib/workflow'

/**
 * The "Workflow" box shown on a bid's recap page (BidHome), between the AI-usage card and the
 * documents grid. It surfaces the pre-sales workflow progress and a button to open the full
 * workflow view. The RAG-seeding actions that used to live here (seed factory knowledge, upload
 * RFP, upload reference document) were retired: ingestion is now covered by SharePoint indexation.
 */
export function WorkflowCard({
  bid,
  onOpenWorkflow,
}: {
  bid: string
  onOpenWorkflow: () => void
}) {
  const { t } = useLang()

  const wfQuery = useQuery({
    queryKey: ['workflow', bid],
    queryFn: () => api.workflow(bid),
    enabled: Boolean(bid),
    retry: false,
  })
  const workflow = wfQuery.data
  const hasWorkflow = Boolean(workflow && workflow.nodes.length > 0)
  const progress = hasWorkflow ? deriveProgress(workflow!.nodes) : null

  return (
    <section className="bidhome-workflow" aria-label={t('wf_card_title')}>
      <div className="bidwf-head">
        <span className="home-eyebrow">{t('wf_card_title')}</span>
        <span className="bidwf-exp" title={t('wf_experimental_note')}>{t('wf_experimental')}</span>
        <button className="bidwf-open" onClick={onOpenWorkflow}>
          {t('wf_open')} <span aria-hidden="true">→</span>
        </button>
      </div>

      <p className="bidwf-exp-note">{t('wf_experimental_note')}</p>

      {progress ? (
        <div className="bidwf-progress">
          <div className="dash-progress-bar-wrap">
            <div
              className="dash-progress-bar"
              style={{ width: `${progress.pct}%` }}
              role="progressbar"
              aria-valuenow={progress.pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${progress.pct}% ${t('wf_nodes_done')}`}
            />
          </div>
          <span className="dash-progress-label">
            {progress.valide}/{progress.total} {t('wf_nodes_done')} · {progress.pct}%
          </span>
          <span
            className={`dash-critical-badge ${progress.criticalPathBlocked ? 'critical-blocked' : 'critical-ok'}`}
          >
            {progress.criticalPathBlocked ? '⚠ ' + t('wf_critical_blocked') : '✓ ' + t('wf_critical_ok')}
          </span>
        </div>
      ) : (
        <p className="bidwf-none">{t('wf_none')}</p>
      )}
    </section>
  )
}
